import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { PRACTICE_PANELS } from "@/data/content";
import { useSEO } from "@/lib/useSEO";

const Field = ({ label, children }) => (
  <div>
    <p className="mono-label-dark">{label}</p>
    <div className="mt-3">{children}</div>
  </div>
);

const Panel = ({ panel, open, onToggle }) => (
  <div data-testid={`practice-panel-${panel.id}`} className="border-b border-navy/10">
    <button
      data-testid={`practice-panel-toggle-${panel.id}`}
      onClick={onToggle}
      aria-expanded={open}
      className="group flex w-full items-center justify-between gap-6 py-8 text-left"
    >
      <div className="flex items-baseline gap-6">
        <span className={`h-px w-8 transition-colors duration-300 ${open ? "bg-gold" : "bg-navy/25 group-hover:bg-gold"}`} />
        <h2 className={`font-serif text-2xl font-medium tracking-tight transition-colors duration-300 sm:text-3xl ${open ? "text-navy" : "text-navy/70 group-hover:text-navy"}`}>
          {panel.title}
        </h2>
      </div>
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center border transition-all duration-500 ${open ? "rotate-45 border-gold text-gold" : "border-navy/25 text-navy/50 group-hover:border-gold group-hover:text-gold"}`}>
        <Plus size={15} />
      </span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <div className="grid gap-10 pb-12 pl-0 sm:pl-14 lg:grid-cols-2">
            <div className="space-y-8">
              <Field label="Nature of Proceedings">
                <div className="flex flex-wrap gap-2">
                  {panel.nature.map((n) => (
                    <span key={n} className="border border-navy/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-navy/70">{n}</span>
                  ))}
                </div>
              </Field>
              <Field label="Court or Authority Ordinarily Involved">
                <p className="text-sm leading-relaxed text-navy/65">{panel.courts}</p>
              </Field>
              <Field label="Common Procedural Stages">
                <ol className="space-y-2">
                  {panel.stages.map((s, i) => (
                    <li key={s} className="flex items-baseline gap-3 text-sm text-navy/65">
                      <span className="font-mono text-[10px] text-gold">{String(i + 1).padStart(2, "0")}</span>{s}
                    </li>
                  ))}
                </ol>
              </Field>
            </div>
            <div className="space-y-8">
              <Field label="Documents Ordinarily Relevant">
                <ul className="space-y-2">
                  {panel.documents.map((d) => (
                    <li key={d} className="flex items-start gap-3 text-sm text-navy/65">
                      <span className="mt-2 h-px w-4 shrink-0 bg-gold" />{d}
                    </li>
                  ))}
                </ul>
              </Field>
              <Field label="Possible Legal Remedies">
                <ul className="space-y-2">
                  {panel.remedies.map((r) => (
                    <li key={r} className="flex items-start gap-3 text-sm text-navy/65">
                      <span className="mt-2 h-px w-4 shrink-0 bg-gold" />{r}
                    </li>
                  ))}
                </ul>
              </Field>
              <Field label="Important Limitations">
                <p className="border-l-2 border-oxblood/60 pl-4 text-sm leading-relaxed text-navy/60">{panel.limitations}</p>
              </Field>
              <div className="flex flex-wrap gap-4 pt-2">
                <Link to="/legal-insights" data-testid={`practice-panel-insight-${panel.id}`} className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold underline-offset-4 hover:underline">
                  Related: {panel.related}
                </Link>
                <Link to="/contact" data-testid={`practice-panel-contact-${panel.id}`} className="btn-outline-dark !px-5 !py-2.5">
                  Contact Office <ArrowRight size={11} />
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default function PracticeAreas() {
  useSEO("Areas of Practice | Smart Legal Solutions", "General information about selected litigation, advisory and documentation areas undertaken subject to conflict checks and formal engagement.");
  const [open, setOpen] = useState("criminal");

  return (
    <div data-testid="practice-areas-page" className="bg-warm pt-32">
      <div className="mx-auto max-w-7xl px-5 pb-24 lg:px-10">
        <Reveal>
          <p className="mono-label-dark">Smart Legal Solutions</p>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl font-medium leading-[1.1] tracking-tight text-navy sm:text-5xl lg:text-6xl">
            Areas of Practice
          </h1>
          <div className="gold-hairline mt-8" />
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-navy/60">
            This page provides general information about selected areas in which professional legal services may be
            undertaken, subject to conflict checks, document review, applicable law and formal engagement.
          </p>
        </Reveal>
        <div className="mt-16 border-t border-navy/10">
          {PRACTICE_PANELS.map((p) => (
            <Panel key={p.id} panel={p} open={open === p.id} onToggle={() => setOpen(open === p.id ? null : p.id)} />
          ))}
        </div>
        <Reveal className="mt-14 border border-navy/15 bg-ivory p-8 sm:p-10">
          <p className="font-mono text-[11px] leading-relaxed tracking-[0.06em] text-slate">
            No remedy or outcome is promised or implied. Whether a proceeding is available and appropriate depends on the
            facts, documents, limitation and procedural stage of each matter.
          </p>
        </Reveal>
      </div>
    </div>
  );
}
